import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import {
  Collection,
  CollectionKindDiscriminants,
} from "../../app/backendTypes";
import { useContext, useRef, useState } from "react";
import { Clear, Close } from "@mui/icons-material";
import * as Yup from "yup";
import { Field, FieldInputProps, FieldMetaProps, Formik } from "formik";
import { LoadingButton } from "@mui/lab";
import Avatar from "../common/Avatar";
import { HttpClientContext, X_REQUEST_ID } from "../../app/client";
import { AxiosError } from "axios";
import Morselbar from "../common/Morselbar";
import UserPicker from "../common/users/UserPicker";

export function SetCollection(props: {
  values?: Collection;
  callback: () => void;
}) {
  const { values, callback } = props;

  const isNew = props.values === undefined;

  if (values !== undefined) {
    if (
      values.kind.inner.fallback_owner === undefined ||
      values.kind.inner.fallback_owner === null
    ) {
      values.kind.inner.fallback_owner = 0;
    }
  }

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [messageOpen, setMessageOpen] = useState(false);
  const [message, setMessage] = useState<{
    error?: string;
    content: string;
  }>({ content: "" });

  const setCollectionSchema = Yup.object({
    id: Yup.string().uuid(),
    info: Yup.object().shape({
      display_name: Yup.string().required(),
      short_desc: Yup.string(),
      long_desc: Yup.string(),
      avatar: Yup.string(),
    }),
    kind: Yup.object().shape({
      kind: Yup.string()
        .oneOf(Object.keys(CollectionKindDiscriminants))
        .required(),
      inner: Yup.object().when("kind", {
        is: CollectionKindDiscriminants.BigQueryMim,
        then: (schema) =>
          schema.shape({
            credentials: Yup.string().required(),
            project_id: Yup.string().required(),
            project_alias: Yup.string(),
            owner_role: Yup.string().required(),
            allow_write_access: Yup.bool(),
            fallback_owner: Yup.number().min(0),
          }),
      }),
    }),
  });

  const client = useContext(HttpClientContext);

  const handleSubmit = (values: Collection) => {
    setLoading(true);
    setMessageOpen(false);
    setMessage({ content: "" });

    // This is basically cheating
    const payload = structuredClone(values) as { id?: string };
    if (payload.id !== undefined && payload.id === "") payload.id = undefined;

    client
      ?.post("/api/datasets", payload)
      .then(() => {
        setMessage({
          error: undefined,
          content: "Collection was added successfully",
        });
        setMessageOpen(true);
        setOpen(false);
        callback();
      })
      .catch((e: AxiosError) => {
        setMessage({
          error: e.response?.headers[X_REQUEST_ID],
          content: "Failed to add new collection",
        });
        setMessageOpen(true);
      })
      .finally(() => setLoading(false));
  };

  const avatarInputRef = useRef<HTMLInputElement>(null);
  const credentialsInputRef = useRef<HTMLInputElement>(null);

  return (
    <>
      <Button variant="outlined" onClick={() => setOpen(true)}>
        {isNew ? "Add Collection" : "Edit Collection"}
      </Button>

      <Dialog open={open} fullWidth>
        <DialogContent>
          <Formik
            initialValues={
              values !== undefined
                ? values
                : ({
                    id: "",
                    info: {
                      display_name: "",
                      short_desc: "",
                      long_desc: "",
                      avatar: "",
                    },
                    kind: {
                      kind: "",
                      inner: {
                        credentials: "",
                        project_id: "",
                        project_alias: "",
                        owner_role: "",
                        allow_write_access: false,
                        fallback_owner: 0,
                      },
                    },
                  } as unknown as Collection)
            }
            onSubmit={handleSubmit}
            validationSchema={setCollectionSchema}
          >
            {({ getFieldMeta, values, handleSubmit, setFieldValue }) => {
              const credsMeta = getFieldMeta("kind.inner.credentials");

              return (
                <Box
                  component="form"
                  onSubmit={(...args) => {
                    handleSubmit(...args);
                  }}
                  sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    <Typography variant="h5">
                      {isNew
                        ? "Create a new collection"
                        : "Modify existing collection"}
                    </Typography>
                    <IconButton onClick={() => setOpen(false)}>
                      <Close />
                    </IconButton>
                  </Box>

                  <Avatar
                    alt={values.info.display_name}
                    src={values.info.avatar}
                  />
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <input
                      disabled={loading}
                      ref={avatarInputRef}
                      type="file"
                      onChange={(e) => {
                        const reader = new FileReader();
                        const file = e.target.files?.[0];

                        if (file) {
                          reader.onload = () =>
                            setFieldValue("info.avatar", reader.result);
                          reader.readAsDataURL(file);
                        } else {
                          setFieldValue("info.avatar", "");
                        }
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => {
                        setFieldValue("info.avatar", "");
                        if (avatarInputRef.current) {
                          avatarInputRef.current.value = "";
                        }
                      }}
                    >
                      <Clear fontSize="small" />
                    </IconButton>
                  </Box>

                  <Field name="info.display_name">
                    {({ field }: { field: FieldInputProps<unknown> }) => {
                      const meta = getFieldMeta("info.display_name");

                      return (
                        <TextField
                          disabled={loading}
                          error={meta.touched && Boolean(meta.error)}
                          helperText={
                            meta.touched && Boolean(meta.error) && meta.error
                          }
                          label="Display Name"
                          size="small"
                          {...field}
                        />
                      );
                    }}
                  </Field>

                  <Field name="info.short_desc">
                    {({ field }: { field: FieldInputProps<unknown> }) => {
                      const meta = getFieldMeta("info.short_desc");

                      return (
                        <TextField
                          disabled={loading}
                          error={meta.touched && Boolean(meta.error)}
                          helperText={
                            meta.touched && Boolean(meta.error) && meta.error
                          }
                          label="Short Description"
                          size="small"
                          {...field}
                        />
                      );
                    }}
                  </Field>

                  <Field name="info.long_desc">
                    {({ field }: { field: FieldInputProps<unknown> }) => {
                      const meta = getFieldMeta("info.long_desc");

                      return (
                        <TextField
                          disabled={loading}
                          error={meta.touched && Boolean(meta.error)}
                          helperText={
                            meta.touched && Boolean(meta.error) && meta.error
                          }
                          label="Long Description"
                          size="small"
                          multiline
                          rows={4}
                          {...field}
                        />
                      );
                    }}
                  </Field>

                  <Field name="kind.kind">
                    {({ field }: { field: FieldInputProps<unknown> }) => {
                      const meta = getFieldMeta("kind.kind");

                      return (
                        <FormControl>
                          <Select
                            size="small"
                            error={meta.touched && Boolean(meta.error)}
                            disabled={loading || values.id !== ""}
                            displayEmpty
                            renderValue={(val) =>
                              val === "" ? (
                                <Typography
                                  color={
                                    meta.touched && Boolean(meta.error)
                                      ? "error"
                                      : "text.secondary"
                                  }
                                >
                                  Pick a collection kind
                                </Typography>
                              ) : (
                                (val as string)
                              )
                            }
                            {...field}
                          >
                            {Object.keys(CollectionKindDiscriminants).map(
                              (k, i) => (
                                <MenuItem key={i} value={k}>
                                  {k}
                                </MenuItem>
                              )
                            )}
                          </Select>
                          {meta.touched && Boolean(meta.error) && (
                            <FormHelperText error>{meta.error}</FormHelperText>
                          )}
                        </FormControl>
                      );
                    }}
                  </Field>

                  {values.kind.kind ===
                    CollectionKindDiscriminants.BigQueryMim && (
                    <>
                      <br />
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <FormControl>
                          <label>
                            Credentials:{" "}
                            {values.kind.inner.credentials !== "" ? (
                              <Typography component="span" color="success.main">
                                ok
                              </Typography>
                            ) : (
                              <Typography component="span" color="error">
                                not set
                              </Typography>
                            )}
                          </label>
                          <input
                            disabled={loading}
                            ref={credentialsInputRef}
                            type="file"
                            onChange={(e) => {
                              const reader = new FileReader();
                              const file = e.target.files?.[0];

                              if (file) {
                                reader.onload = () =>
                                  setFieldValue(
                                    "kind.inner.credentials",
                                    reader.result
                                  );
                                reader.readAsText(file);
                              } else {
                                setFieldValue("kind.inner.credentials", "");
                              }
                            }}
                          />
                          {credsMeta.touched && Boolean(credsMeta.error) && (
                            <FormHelperText error>
                              {credsMeta.error}
                            </FormHelperText>
                          )}
                        </FormControl>
                        <IconButton
                          disabled={loading}
                          size="small"
                          onClick={() => {
                            setFieldValue("kind.inner.credentials", "");
                            if (credentialsInputRef.current) {
                              credentialsInputRef.current.value = "";
                            }
                          }}
                        >
                          <Clear fontSize="small" />
                        </IconButton>
                      </Box>
                      <br />

                      <Field name="kind.inner.project_id">
                        {({ field }: { field: FieldInputProps<unknown> }) => {
                          const meta = getFieldMeta("kind.inner.project_id");

                          return (
                            <TextField
                              disabled={loading || values.id !== ""}
                              error={meta.touched && Boolean(meta.error)}
                              helperText={
                                meta.touched &&
                                Boolean(meta.error) &&
                                meta.error
                              }
                              label="Project Id"
                              size="small"
                              {...field}
                            />
                          );
                        }}
                      </Field>

                      <Field name="kind.inner.project_alias">
                        {({ field }: { field: FieldInputProps<unknown> }) => {
                          const meta = getFieldMeta("kind.inner.project_alias");

                          return (
                            <TextField
                              disabled={loading || values.id !== ""}
                              error={meta.touched && Boolean(meta.error)}
                              helperText={
                                meta.touched &&
                                Boolean(meta.error) &&
                                meta.error
                              }
                              label="Project Alias"
                              size="small"
                              {...field}
                            />
                          );
                        }}
                      </Field>

                      <Field name="kind.inner.owner_role">
                        {({ field }: { field: FieldInputProps<unknown> }) => {
                          const meta = getFieldMeta("kind.inner.owner_role");

                          return (
                            <TextField
                              error={meta.touched && Boolean(meta.error)}
                              helperText={
                                meta.touched &&
                                Boolean(meta.error) &&
                                meta.error
                              }
                              label="Owner Role"
                              size="small"
                              {...field}
                            />
                          );
                        }}
                      </Field>

                      <Field name="kind.inner.fallback_owner">
                        {({ field }: { field: FieldInputProps<unknown> }) => {
                          const meta = getFieldMeta(
                            "kind.inner.fallback_owner"
                          );

                          return (
                            <UserPicker
                              small
                              initialValue={field.value as number}
                              onChange={(user) =>
                                setFieldValue(
                                  "kind.inner.fallback_owner",
                                  user?.employee.id
                                )
                              }
                              meta={meta as FieldMetaProps<string>}
                              placeholder="Pick a fallback owner"
                            />
                          );
                        }}
                      </Field>

                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={values.kind.inner.allow_write_access}
                            onChange={(e) =>
                              setFieldValue(
                                "kind.inner.allow_write_access",
                                e.target.checked
                              )
                            }
                            disabled={loading}
                          />
                        }
                        label="Allow write access"
                      />
                    </>
                  )}

                  <Box display="flex" justifyContent="flex-end">
                    <LoadingButton
                      type="submit"
                      loading={loading}
                      variant="outlined"
                    >
                      Confirm
                    </LoadingButton>
                  </Box>
                </Box>
              );
            }}
          </Formik>
        </DialogContent>
      </Dialog>

      <Morselbar
        open={messageOpen}
        setOpen={setMessageOpen}
        content={message.content}
        error={message.error}
      />
    </>
  );
}
